// src/components/admin/FeedbackManager.tsx
import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';
import toast from 'react-hot-toast';

const client = generateClient<Schema>();

type FeedbackItem = Schema['Feedback']['type'];
type FilterStatus = 'all' | 'new' | 'reviewing' | 'resolved' | 'wontfix';
type FilterCategory = 'all' | 'bug' | 'feature' | 'ui' | 'general';

export default function FeedbackManager() {
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('new');
  const [filterCategory, setFilterCategory] = useState<FilterCategory>('all');
  const [selectedItem, setSelectedItem] = useState<FeedbackItem | null>(null);

  useEffect(() => {
    loadFeedback();
  }, []);

const loadFeedback = async () => {
  try {
    setLoading(true);
    void ['🔍 Attempting to load feedback...'];
    
    const result = await client.models.Feedback.list();
    
    void ['✅ Feedback result:', result];
    void ['✅ Feedback data:', result.data];
    void ['✅ Feedback errors:', result.errors];
    
    if (result.errors && result.errors.length > 0) {
      void ['❌ Authorization errors:', result.errors];
      toast.error(`Authorization error: ${result.errors[0].message}`);
      return;
    }
    
    const sorted = (result.data || []).sort((a, b) => 
      new Date(b.timestamp!).getTime() - new Date(a.timestamp!).getTime()
    );
    setFeedback(sorted as FeedbackItem[]);
    void [`✅ Loaded ${sorted.length} feedback items`];
  } catch (error) {
    void ['💥 Load feedback error:', error];
    toast.error('Failed to load feedback');
  } finally {
    setLoading(false);
  }
};

  const updateStatus = async (id: string, status: string, adminNotes?: string) => {
    try {
      await client.models.Feedback.update({
        id,  // ✅ Changed from feedbackId
        status,
        adminNotes: adminNotes || undefined,
        resolvedAt: status === 'resolved' ? new Date().toISOString() : undefined,
      });
      
      toast.success('Status updated!');
      await loadFeedback();
      setSelectedItem(null);
    } catch (error) {
      void ['Update status error:', error];
      toast.error('Failed to update status');
    }
  };

  const deleteFeedback = async (id: string) => {
    if (!confirm('Are you sure you want to delete this feedback?')) return;
    
    try {
      await client.models.Feedback.delete({ id });  // ✅ Changed from feedbackId
      toast.success('Feedback deleted');
      await loadFeedback();
      setSelectedItem(null);
    } catch (error) {
      void ['Delete feedback error:', error];
      toast.error('Failed to delete feedback');
    }
  };

  const filteredFeedback = feedback.filter(item => {
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    if (filterCategory !== 'all' && item.category !== filterCategory) return false;
    return true;
  });

  const categoryIcons: Record<string, string> = {
    bug: '🐛',
    feature: '💡',
    ui: '🎨',
    general: '💬',
  };

  const statusColors: Record<string, string> = {
    new: 'bg-blue-100 text-blue-800',
    reviewing: 'bg-yellow-100 text-yellow-800',
    resolved: 'bg-green-100 text-green-800',
    wontfix: 'bg-gray-100 text-gray-800',
  };

  const stats = {
    total: feedback.length,
    new: feedback.filter(f => f.status === 'new').length,
    reviewing: feedback.filter(f => f.status === 'reviewing').length,
    resolved: feedback.filter(f => f.status === 'resolved').length,
  };

  if (loading) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl border-2 border-brand/20 p-16 text-center shadow-lg">
        <div className="text-6xl mb-4 animate-spin">⏳</div>
        <p className="text-lg text-gray-600">Loading feedback...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="📊" label="Total" value={stats.total} color="from-blue-500 to-blue-600" />
        <StatCard icon="🆕" label="New" value={stats.new} color="from-green-500 to-green-600" />
        <StatCard icon="👀" label="Reviewing" value={stats.reviewing} color="from-yellow-500 to-yellow-600" />
        <StatCard icon="✅" label="Resolved" value={stats.resolved} color="from-purple-500 to-purple-600" />
      </div>

      {/* Filters */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl border-2 border-brand/20 p-6 shadow-lg">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Status Filter */}
          <div className="flex-1">
            <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="reviewing">Reviewing</option>
              <option value="resolved">Resolved</option>
              <option value="wontfix">Won't Fix</option>
            </select>
          </div>

          {/* Category Filter */}
          <div className="flex-1">
            <label className="block text-sm font-bold text-gray-700 mb-2">Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as FilterCategory)}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
            >
              <option value="all">All Categories</option>
              <option value="bug">🐛 Bugs</option>
              <option value="feature">💡 Features</option>
              <option value="ui">🎨 UI/UX</option>
              <option value="general">💬 General</option>
            </select>
          </div>
        </div>
      </div>

      {/* Feedback List */}
      <div className="space-y-4">
        {filteredFeedback.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border-2 border-brand/20 p-12 text-center shadow-lg">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No feedback found</h3>
            <p className="text-gray-600">Try adjusting your filters</p>
          </div>
        ) : (
          filteredFeedback.map((item) => (
            <FeedbackCard
              key={item.id}  // ✅ Changed from feedbackId
              item={item}
              categoryIcon={categoryIcons[item.category || 'general']}
              statusColor={statusColors[item.status || 'new']}
              onSelect={() => setSelectedItem(item)}
            />
          ))
        )}
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <FeedbackDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdateStatus={updateStatus}
          onDelete={deleteFeedback}
        />
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }: { icon: string; label: string; value: number; color: string }) {
  return (
    <div className={`bg-gradient-to-br ${color} text-white rounded-xl p-6 shadow-lg`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold opacity-90">{label}</div>
          <div className="text-3xl font-black mt-1">{value}</div>
        </div>
        <div className="text-4xl opacity-80">{icon}</div>
      </div>
    </div>
  );
}

function FeedbackCard({ item, categoryIcon, statusColor, onSelect }: any) {
  return (
    <div
      onClick={onSelect}
      className="bg-white/80 backdrop-blur-sm rounded-xl border-2 border-gray-200 hover:border-brand p-6 shadow-md hover:shadow-xl transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{categoryIcon}</span>
            <h3 className="font-bold text-gray-900 text-lg truncate">{item.title}</h3>
          </div>
          <p className="text-gray-600 text-sm line-clamp-2 mb-3">{item.description}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className={`px-2 py-1 rounded-full font-semibold ${statusColor}`}>
              {item.status}
            </span>
            <span className="text-gray-500">
              {new Date(item.timestamp!).toLocaleDateString()}
            </span>
            <span className="text-gray-500">•</span>
            <span className="text-gray-500">{item.currentPage}</span>
          </div>
        </div>
        <svg className="w-6 h-6 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

function FeedbackDetailModal({ item, onClose, onUpdateStatus, onDelete }: any) {
  const [adminNotes, setAdminNotes] = useState(item.adminNotes || '');
  const [newStatus, setNewStatus] = useState(item.status || 'new');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-brand to-branddark text-white px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Feedback Details</h2>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Title */}
          <div>
            <div className="text-sm font-semibold text-gray-500 mb-1">Title</div>
            <div className="text-xl font-bold text-gray-900">{item.title}</div>
          </div>

          {/* Description */}
          <div>
            <div className="text-sm font-semibold text-gray-500 mb-2">Description</div>
            <div className="text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-4">
              {item.description}
            </div>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoItem label="Category" value={item.category} />
            <InfoItem label="Status" value={item.status} />
            <InfoItem label="Page" value={item.currentPage} />
            <InfoItem label="Submitted" value={new Date(item.timestamp!).toLocaleString()} />
            <InfoItem label="User Email" value={item.userEmail || 'N/A'} />
            <InfoItem label="Screen Size" value={item.screenWidth || 'N/A'} />
          </div>

          {/* User Agent */}
          <div>
            <div className="text-sm font-semibold text-gray-500 mb-2">Browser Info</div>
            <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 font-mono break-all">
              {item.userAgent}
            </div>
          </div>

          {/* Status Update */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Update Status</label>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand"
            >
              <option value="new">New</option>
              <option value="reviewing">Reviewing</option>
              <option value="resolved">Resolved</option>
              <option value="wontfix">Won't Fix</option>
            </select>
          </div>

          {/* Admin Notes */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Admin Notes</label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={4}
              placeholder="Add internal notes..."
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200 flex justify-between">
          <button
            onClick={() => onDelete(item.id)}  // ✅ Changed from feedbackId
            className="px-6 py-2 text-red-600 hover:bg-red-50 font-bold rounded-lg transition-colors"
          >
            Delete
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={() => onUpdateStatus(item.id, newStatus, adminNotes)}  // ✅ Changed from feedbackId
              className="px-6 py-2 bg-gradient-to-r from-brand to-branddark text-white font-bold rounded-lg hover:shadow-lg"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 mb-1">{label}</div>
      <div className="text-sm text-gray-900 font-medium">{value}</div>
    </div>
  );
}